/**
 * ExplanationScreen.tsx - Explanation of the framing effect
 * Last updated: 2025-08-14 14:09:00
 * Updated by: JulianPolly (glossary import, icons for experiment/sources, title fix)
 */

import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@blueprintjs/core";
import { ScreenConfig } from "../Sequences";
import NapkinImg from "./assets/napkin.png";
// --- NEW: Import glossary from translations ---
import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";
import { useLanguage } from "../../../../context/LanguageContext";

// --- Helper: get glossary for current language ---
function useGlossaryTerms(language: string) {
    if (language === "de") {
        return deTranslations.glossary?.terms || {};
    } else if (language === "en") {
        return enTranslations.glossary?.terms || {};
    }
    // fallback: use German
    return deTranslations.glossary?.terms || {};
}

// --- Hilfsfunktion für Glossar-Alias-Auflösung (language-agnostic) ---
function resolveGlossarEntry(
    glossar: Record<string, any>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// Tooltip für Glossar-Begriff (alias-sicher, multi-language!)
const BegriffTooltip: React.FC<{ begriff: string; children: React.ReactNode; glossar: Record<string, any> }> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: "#e7f1fa",
                color: "#2563eb",
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible((v: boolean) => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 320,
                            maxWidth: 600,
                            background: "#fff",
                            color: "#222",
                            border: "1.5px solid #bcd3f6",
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "22px 24px",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-wrap",
                            marginTop: 12
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// --- Case-insensitive glossary parsing, multi-language ---
function parseWithGlossarTooltip(
    text: string,
    terms: string[],
    used: Set<string>,
    glossar: Record<string, any>
) {
    if (typeof text !== "string") return null;
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => { glossaryKeyMap[k.toLowerCase()] = k; });

    const re = new RegExp(
        `\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
        "gi"
    );
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;

        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(
                <BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>
                    {matched}
                </BegriffTooltip>
            );
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
}

export const ExplainationScreenComponent: React.FC = () => {
    const { t, language } = useLanguage();
    const glossar = useGlossaryTerms(language);

    // Titel und Bildunterschrift aus Übersetzungen laden
    const title = t('experiments', 'LGFraming', 'screens', 'explanation', 'title') ||
        "Wie Worte unsere Entscheidungen lenken: Gewinn- und Verlustframing";
    const imageAlt = t('experiments', 'LGFraming', 'screens', 'explanation', 'imageAlt') ||
        "Serviette mit Notizen";
    const sourcesTitle = t('experiments', 'LGFraming', 'screens', 'explanation', 'sources', 'title') ||
        "Quellen:";

    // Die originalen, festen Abschnitte beibehalten
    const SECTION_TITLES = [
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'whatIsIt', 'title') || "Was ist Gewinn- und Verlustframing?",
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'howItFeels', 'title') || "Wie fühlt es sich an?",
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'whyWeReact', 'title') || "Warum reagieren wir so?"
    ];

    const paragraphs = [
        // Was ist Gewinn- und Verlustframing?
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'whatIsIt', 'content') ||
        "Gewinn- und Verlustframing bedeutet, dass wir dieselbe Information unterschiedlich bewerten, je nachdem, ob sie als Gewinn oder als Verlust dargestellt wird. Die Formulierung beeinflusst, ob wir etwas als positiv oder negativ wahrnehmen – und das steuert unser Verhalten, ohne dass sich an den eigentlichen Fakten etwas ändert.",

        // Wie fühlt es sich an?
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'howItFeels', 'content') ||
        "Verluste treffen uns emotional viel stärker als gleich große Gewinne uns freuen. Wenn wir 10 Euro verlieren, ärgert uns das deutlich mehr, als uns das Gewinnen von 10 Euro glücklich macht. Die Freude über Gewinne wächst langsam, aber jeder weitere Verlust schmerzt sofort besonders.",

        // Warum reagieren wir so?
        t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'whyWeReact', 'content') ||
        "Dieses Verhalten nennt man Verlustaversion. Die sogenannte Prospect Theory erklärt, warum: Wir vergleichen alles mit einem Ausgangspunkt, unserem Referenzpunkt. Liegen wir darüber, empfinden wir es als Gewinn – liegen wir darunter, als Verlust. Und weil Verluste für uns besonders schlimm sind, lassen wir uns von der Formulierung schnell beeinflussen – selbst wenn es eigentlich um genau dieselbe Sache geht."
    ];

    // Experiment-Box-Text aus Übersetzungsdatei laden
    const experimentNoteTitle = t('experiments', 'LGFraming', 'screens', 'explanation', 'sections', 'experiment', 'title');
    const experimentNoteText = t('experiments', 'LGFraming', 'screens', 'explanation', 'experiment', 'content') ||
        "Auch in unserem Experiment habt ihr erlebt, wie unterschiedlich eine Situation durch Framing wirken kann. Euer Referenzpunkt war dabei das neue Smartphone – also der Zustand, dass es noch ganz und unbeschädigt ist. Im Gewinn-Frame wurden vor allem die Vorteile einer Versicherung hervorgehoben. Im Verlust-Frame stand dagegen im Mittelpunkt, welche Nachteile und möglichen Verluste euch drohen, wenn ihr keine Versicherung abschließt.";

    // Fix: Only show experiment note title if it exists and is not literally "title"
    const showExperimentNoteTitle =
        experimentNoteTitle &&
        experimentNoteTitle.toLowerCase() !== "title" &&
        experimentNoteTitle.trim() !== "";

    // Quellen aus Übersetzungsdatei laden
    const sourcesAPA = t('experiments', 'LGFraming', 'screens', 'explanation', 'sources', 'items', { returnObjects: true }) as string[] || [
        "Tversky, A., & Kahneman, D. (1981). The framing of decisions and the psychology of choice. Science, 211(4481), 453–458.",
        "Kahneman, D., & Tversky, A. (1984). Choices, values, and frames. American Psychologist, 39(4), 341–350."
    ];

    // Beispiel-Box zu unserem Experiment (Karte) - jetzt mit Icon!
    const ownExperimentNote = (
        <div
            style={{
                marginTop: 38,
                marginBottom: 38,
                background: "#f6f8fa",
                borderRadius: 11,
                padding: "26px 32px",
                fontSize: 17,
                lineHeight: 1.7,
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                color: "#444",
                textAlign: "justify"
            }}
        >
            <div style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: showExperimentNoteTitle ? 16 : 0
            }}>
                <Icon
                    icon="lab-test"
                    size={20}
                    style={{
                        marginRight: showExperimentNoteTitle ? 12 : 0,
                        marginTop: 2,
                        color: "#2563eb"
                    }}
                />
                {showExperimentNoteTitle && (
                    <span style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: "#2563eb"
                    }}>
                        {experimentNoteTitle}
                    </span>
                )}
            </div>
            {experimentNoteText}
        </div>
    );

    // Die originale Funktion zur Erstellung des Inhalts
    function parseParagraphsWithSections() {
        const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
        const used = new Set<string>();
        const parsed: React.ReactNode[] = [];

        for (let i = 0; i < SECTION_TITLES.length; ++i) {
            parsed.push(
                <div key={"section-title-" + i} style={{ marginTop: i === 0 ? 0 : 30, marginBottom: 8 }}>
                    <b style={{ fontSize: 20 }}>{SECTION_TITLES[i]}</b>
                </div>
            );
            // Bild nur im ersten Abschnitt, oben rechts
            if (i === 0) {
                parsed.push(
                    <img
                        src={NapkinImg}
                        alt={imageAlt}
                        style={{
                            float: "right",
                            marginLeft: 44,
                            marginBottom: 18,
                            width: 260,
                            maxWidth: "44%",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                            objectFit: "cover"
                        }}
                    />
                );
            }
            parsed.push(
                <p key={`p-${i}`} style={{ marginTop: 0, marginBottom: 18, whiteSpace: "pre-line" }}>
                    {parseWithGlossarTooltip(paragraphs[i], terms, used, glossar)}
                </p>
            );
        }
        return parsed;
    }

    return (
        <div style={{
            maxWidth: 1250,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
            padding: "44px 64px"
        }}>
            <h2 style={{ marginBottom: 32 }}>{title}</h2>
            <div style={{
                fontSize: 17,
                lineHeight: 1.7,
                textAlign: "justify",
                position: "relative",
                minHeight: 260
            }}>
                {parseParagraphsWithSections()}
            </div>

            {ownExperimentNote}

            <div style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "22px 24px",
                marginTop: 36,
                fontSize: 15.5,
                color: "#444"
            }}>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8
                }}>
                    <Icon
                        icon="book"
                        size={16}
                        style={{
                            marginRight: 10,
                            color: "#2563eb"
                        }}
                    />
                    <b>{sourcesTitle}</b>
                </div>
                <ul style={{marginTop: 8, marginBottom: 0, paddingLeft: 24}}>
                    {sourcesAPA.map((src, index) => (
                        <li key={`source-${index}`}>{src}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export const ExplainationScreen: ScreenConfig = {
    component: ExplainationScreenComponent,
    defaultResult: {},
    canContinueDefault: true
};